import React, { useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { ArrowUpRight, CheckCircle } from "@phosphor-icons/react";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PROJECT_TYPES = [
    "Site web",
    "Photographie",
    "Production vidéo",
    "Logo / Identité",
    "Design graphique",
    "Plusieurs services",
    "Autre",
];

const BUDGETS = [
    "< 2 000 $",
    "2 000 $ – 5 000 $",
    "5 000 $ – 10 000 $",
    "10 000 $ – 25 000 $",
    "25 000 $ +",
    "À discuter",
];

const initialState = {
    name: "",
    email: "",
    project_type: "",
    budget: "",
    message: "",
};

export default function Contact() {
    const [form, setForm] = useState(initialState);
    const [loading, setLoading] = useState(false);
    const [done, setDone] = useState(false);

    const onChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

    const onSubmit = async (e) => {
        e.preventDefault();
        if (
            !form.name.trim() ||
            !form.email.trim() ||
            !form.project_type ||
            !form.message.trim()
        ) {
            toast.error("Merci de remplir tous les champs requis.");
            return;
        }
        setLoading(true);
        try {
            await axios.post(`${API}/contact`, form);
            setDone(true);
            setForm(initialState);
            toast.success("Demande envoyée. Nous revenons vers vous rapidement.");
        } catch (err) {
            console.error(err);
            toast.error(
                "Une erreur est survenue. Réessayez ou écrivez-nous directement.",
            );
        } finally {
            setLoading(false);
        }
    };

    return (
        <section
            id="contact"
            data-testid="contact-section"
            className="relative py-24 md:py-32 px-6 md:px-12 lg:px-16 border-t border-scd"
        >
            <div className="grid lg:grid-cols-12 gap-12">
                <div className="lg:col-span-5">
                    <motion.div
                        initial={{ y: 40, opacity: 0 }}
                        whileInView={{ y: 0, opacity: 1 }}
                        viewport={{ once: true, amount: 0.3 }}
                        transition={{ duration: 0.7 }}
                    >
                        <span className="label-eyebrow text-yellow">— Contact</span>
                        <h2
                            className="section-heading text-cream mt-6"
                            data-testid="contact-heading"
                        >
                            Démarrons
                            <br />
                            <span className="text-yellow italic">votre projet.</span>
                        </h2>
                        <p className="mt-6 text-cream/75 text-lg max-w-md">
                            Parlons de votre marque, de vos objectifs et de la
                            meilleure façon de mettre votre entreprise en lumière.
                        </p>

                        <div className="mt-10 space-y-5">
                            <ContactLine
                                label="Email"
                                value="bonjour@centdouze.ca"
                                href="mailto:bonjour@centdouze.ca"
                            />
                            <ContactLine
                                label="Localisation"
                                value="Québec, Canada"
                            />
                            <ContactLine
                                label="Disponibilité"
                                value="Acceptons des projets pour 2025"
                            />
                        </div>
                    </motion.div>
                </div>

                <div className="lg:col-span-7">
                    {done ? (
                        <motion.div
                            initial={{ scale: 0.95, opacity: 0 }}
                            animate={{ scale: 1, opacity: 1 }}
                            transition={{ duration: 0.5 }}
                            className="border border-scd bg-elev rounded-sm p-10 text-center"
                            data-testid="contact-success"
                        >
                            <CheckCircle
                                size={56}
                                weight="duotone"
                                className="text-yellow mx-auto"
                            />
                            <h3 className="font-display text-3xl mt-6 text-cream">
                                Merci&nbsp;!
                            </h3>
                            <p className="mt-3 text-cream/75">
                                Votre demande a été envoyée avec succès. Nous vous
                                répondrons sous 24-48&nbsp;h.
                            </p>
                            <button
                                onClick={() => setDone(false)}
                                className="mt-8 inline-flex items-center gap-2 px-6 py-3 rounded-full border border-scd text-cream hover:border-yellow hover:text-yellow transition-colors"
                                data-testid="contact-new-button"
                            >
                                Envoyer une autre demande
                            </button>
                        </motion.div>
                    ) : (
                        <motion.form
                            onSubmit={onSubmit}
                            initial={{ y: 40, opacity: 0 }}
                            whileInView={{ y: 0, opacity: 1 }}
                            viewport={{ once: true, amount: 0.2 }}
                            transition={{ duration: 0.7 }}
                            data-testid="contact-form"
                            className="border border-scd bg-elev rounded-sm p-6 md:p-10 space-y-6"
                        >
                            <div className="grid md:grid-cols-2 gap-6">
                                <Field label="Nom complet *">
                                    <input
                                        name="name"
                                        value={form.name}
                                        onChange={onChange}
                                        required
                                        data-testid="contact-input-name"
                                        className="scd-input"
                                        placeholder="Jane Dupuis"
                                    />
                                </Field>
                                <Field label="Email *">
                                    <input
                                        name="email"
                                        type="email"
                                        value={form.email}
                                        onChange={onChange}
                                        required
                                        data-testid="contact-input-email"
                                        className="scd-input"
                                        placeholder="vous@entreprise.com"
                                    />
                                </Field>
                            </div>

                            <div className="grid md:grid-cols-2 gap-6">
                                <Field label="Type de projet *">
                                    <select
                                        name="project_type"
                                        value={form.project_type}
                                        onChange={onChange}
                                        required
                                        data-testid="contact-select-project-type"
                                        className="scd-input"
                                    >
                                        <option value="">Choisir un service</option>
                                        {PROJECT_TYPES.map((p) => (
                                            <option key={p} value={p}>
                                                {p}
                                            </option>
                                        ))}
                                    </select>
                                </Field>
                                <Field label="Budget approximatif">
                                    <select
                                        name="budget"
                                        value={form.budget}
                                        onChange={onChange}
                                        data-testid="contact-select-budget"
                                        className="scd-input"
                                    >
                                        <option value="">Sélectionner</option>
                                        {BUDGETS.map((b) => (
                                            <option key={b} value={b}>
                                                {b}
                                            </option>
                                        ))}
                                    </select>
                                </Field>
                            </div>

                            <Field label="Décrivez votre projet *">
                                <textarea
                                    name="message"
                                    rows={5}
                                    value={form.message}
                                    onChange={onChange}
                                    required
                                    data-testid="contact-input-message"
                                    className="scd-input resize-none"
                                    placeholder="Parlez-nous de votre entreprise, vos objectifs, votre échéancier..."
                                />
                            </Field>

                            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 pt-2">
                                <span className="text-xs text-cream/60">
                                    Vos informations ne seront jamais partagées.
                                </span>
                                <button
                                    type="submit"
                                    disabled={loading}
                                    data-testid="contact-submit-button"
                                    className="group inline-flex items-center gap-3 px-7 py-4 rounded-full bg-yellow text-[#272727] font-semibold text-base hover:bg-green hover:text-[#faf7f1] transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
                                >
                                    {loading ? "Envoi..." : "Envoyer la demande"}
                                    <ArrowUpRight
                                        size={18}
                                        weight="bold"
                                        className="group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform"
                                    />
                                </button>
                            </div>
                        </motion.form>
                    )}
                </div>
            </div>

            <style>{`
                .scd-input {
                    width: 100%;
                    background: #faf7f1;
                    color: #272727;
                    border: 1px solid rgba(39,39,39,0.18);
                    padding: 0.85rem 1rem;
                    border-radius: 4px;
                    font-family: "Manrope", sans-serif;
                    font-size: 0.95rem;
                    transition: border-color 0.25s ease, box-shadow 0.25s ease;
                }
                .scd-input:focus {
                    outline: none;
                    border-color: #3b5838;
                    box-shadow: 0 0 0 3px rgba(59,88,56,0.18);
                }
                .scd-input::placeholder { color: rgba(39,39,39,0.4); }
                select.scd-input { appearance: none; background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='8' viewBox='0 0 12 8'%3E%3Cpath fill='none' stroke='%23272727' stroke-width='1.5' d='M1 1l5 5 5-5'/%3E%3C/svg%3E"); background-repeat: no-repeat; background-position: right 1rem center; padding-right: 2.5rem; }
            `}</style>
        </section>
    );
}

function Field({ label, children }) {
    return (
        <label className="block">
            <span className="label-eyebrow text-cream/70 mb-2 block">{label}</span>
            {children}
        </label>
    );
}

function ContactLine({ label, value, href }) {
    const content = (
        <div className="flex items-baseline gap-4 border-t border-scd pt-4">
            <span className="label-eyebrow text-cream/60 min-w-[110px]">{label}</span>
            <span className="text-cream font-display text-xl md:text-2xl">{value}</span>
        </div>
    );
    if (href) {
        return (
            <a
                href={href}
                className="block hover:text-yellow [&_span:last-child]:transition-colors [&_span:last-child]:hover:text-yellow"
            >
                {content}
            </a>
        );
    }
    return content;
}
